import React, { Fragment, useState } from "react";

import CollapsedHint from "../CollapsedHint";
import { Dialog, Transition } from "@headlessui/react";
import NotificationBell from "../NotificationBell";
import { SandboxBadge } from "../SandboxBanner";
import SideNavList from "../SideNavList";
import UserMenu from "../UserMenu";
import logo from "../../assets/logo.png";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const STORAGE_KEY = "hackle.sidebarCollapsed";

const CollapseIcon = ({ collapsed }) => (
  <svg
    viewBox='0 0 16 16'
    fill='none'
    className={`w-4 h-4 shrink-0 ${collapsed ? "rotate-180" : ""}`}
    aria-hidden='true'>
    <path
      d='M9.5 3.5 L5.5 8 l4 4.5 M6.5 3.5 L2.5 8 l4 4.5'
      stroke='currentColor'
      strokeWidth='1.4'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    viewBox='0 0 20 20'
    fill='none'
    className='w-5 h-5'
    aria-hidden='true'>
    <path
      d='M5 5l10 10M15 5L5 15'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinecap='round'
    />
  </svg>
);

/** Shared drawer interior used by desktop aside and mobile/tablet overlay. */
const DrawerInterior = ({
  collapsed = false,
  onNavigate,
  onClose,
  showCollapseToggle = false,
  onToggleCollapsed,
}) => {
  const navigate = useNavigate();
  const accessor = useSelector(state => state.accessor.accessor);

  const goHome = () => {
    navigate("/");
    onNavigate?.();
  };

  return (
    <>
      <div
        className={`flex items-center h-16 shrink-0 ${
          collapsed
            ? "justify-center px-0"
            : onClose
            ? "justify-between px-4"
            : "justify-center px-5"
        }`}>
        <CollapsedHint label='Wiffy' disabled={!collapsed}>
          <div className={`flex ${onClose ? "" : "w-full justify-center"}`}>
            <button
              type='button'
              onClick={goHome}
              aria-label='Wiffy home'
              className='flex items-center justify-center'>
              <img
                src={logo}
                alt='Wiffy'
                className={
                  collapsed
                    ? "h-6 w-auto max-w-full object-contain"
                    : onClose
                    ? "h-6 w-auto max-w-[7rem] object-contain"
                    : "h-9 w-auto max-w-[9.5rem] object-contain"
                }
              />
            </button>
          </div>
        </CollapsedHint>
        {onClose && (
          <button
            type='button'
            onClick={onClose}
            aria-label='Close menu'
            className='inline-flex items-center justify-center p-2 rounded-md text-grey-700 hover:bg-grey-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-lightest'>
            <CloseIcon />
          </button>
        )}
      </div>
      <SandboxBadge collapsed={collapsed} />

      <div
        className='flex-1 min-h-0 overflow-y-auto overflow-x-hidden'>
        <SideNavList collapsed={collapsed} onNavigate={onNavigate} />
      </div>

      <div className='shrink-0 border-t border-grey-100'>
        <CollapsedHint label={accessor?.name} disabled={!collapsed}>
          <div
            className={`flex items-center ${
              collapsed
                ? "flex-col-reverse gap-1 py-2"
                : "gap-1 px-1.5 py-1.5"
            }`}>
            <div className={collapsed ? "w-full" : "min-w-0 flex-1"}>
              <UserMenu collapsed={collapsed} />
            </div>
            <NotificationBell
              className={collapsed ? "" : "shrink-0 pr-1"}
            />
          </div>
        </CollapsedHint>
      </div>

      {showCollapseToggle && (
        <CollapsedHint
          label={collapsed ? "Expand" : "Collapse"}
          disabled={!collapsed}>
          <button
            type='button'
            onClick={onToggleCollapsed}
            className={`flex items-center gap-3 w-full shrink-0 border-t border-grey-100 text-base font-medium text-grey-900 hover:bg-grey-50 transition-colors ${
              collapsed ? "justify-center px-0 py-3" : "px-5 py-3"
            }`}>
            <CollapseIcon collapsed={collapsed} />
            {!collapsed && <span>Collapse</span>}
          </button>
        </CollapsedHint>
      )}
    </>
  );
};

export const SideDrawer = () => {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      try {
        window.localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // ignore quota / private mode
      }
      return next;
    });
  };

  return (
    <aside
      className={`hidden lg:flex lg:flex-col shrink-0 h-full bg-white border-r border-grey-100 transition-[width] duration-200 ${
        collapsed ? "w-[4.5rem]" : "w-68"
      }`}>
      <DrawerInterior
        collapsed={collapsed}
        showCollapseToggle
        onToggleCollapsed={toggleCollapsed}
      />
    </aside>
  );
};

/** Off-canvas drawer for mobile and tablet (< lg). Desktop layout is untouched. */
export const MobileSideDrawer = ({ open, onClose }) => {
  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        as='div'
        className='relative z-40 lg:hidden'
        onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-200'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-150'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'>
          <div
            className='fixed inset-0 bg-grey-900/50 backdrop-blur-[1px]'
            aria-hidden='true'
          />
        </Transition.Child>

        <div className='fixed inset-0 overflow-hidden'>
          <div className='absolute inset-0 overflow-hidden'>
            <div className='pointer-events-none fixed inset-y-0 left-0 flex max-w-full'>
              <Transition.Child
                as={Fragment}
                enter='transform transition ease-out duration-200'
                enterFrom='-translate-x-full'
                enterTo='translate-x-0'
                leave='transform transition ease-in duration-150'
                leaveFrom='translate-x-0'
                leaveTo='-translate-x-full'>
                <Dialog.Panel className='pointer-events-auto flex flex-col h-full w-[17rem] max-w-[85vw] bg-white shadow-xl border-r border-grey-100'>
                  <DrawerInterior
                    collapsed={false}
                    onClose={onClose}
                    onNavigate={onClose}
                  />
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

const SideNav = props => {
  return (
    <div
      id='app-content-scroll'
      className='flex flex-col h-full min-h-0 min-w-0 overflow-y-auto bg-grey-50'>
      <div className='p-3 sm:p-4 lg:p-7 min-h-full min-w-0'>{props.children}</div>
    </div>
  );
};

export default SideNav;
